import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  finishSetupAsync,
  joinControlPlaneAsync,
  setupConfigAsync,
  validateLicense,
} from "./setupThunks";
import { ApiError } from "../../api/controlPlaneApi";
import { LoadingStatus } from "common/types";

type SetupSteps = 1 | 2 | 3;

export interface SetupState {
  status: LoadingStatus;
  setupPlan: "register" | "join";
  step: SetupSteps;
  jwt: string;
  jwtError: string;
  joinError: ApiError | null;
  siteName: string;
  endpoint: string;
  psk: string;
  caHash: string;
  uiEndpoint: string;
  apiEndpoint: string;
  apiPort: number;
  setupComplete: boolean;
  username: string;
  password: string;
  passwordRepeat: string;
}

const initialState: SetupState = {
  status: "idle",
  setupPlan: "register",
  step: 1,
  jwt: "",
  jwtError: "",
  joinError: null,
  siteName: "",
  endpoint: "",
  psk: "",
  caHash: "",
  uiEndpoint: "",
  apiEndpoint: "",
  apiPort: 7899,
  setupComplete: false,
  username: "",
  password: "",
  passwordRepeat: "",
};

export const setupSlice = createSlice({
  name: "setup",
  initialState,
  reducers: {
    setSetupPlan: (state, action: PayloadAction<"register" | "join">) => {
      state.setupPlan = action.payload;
    },
    setStep: (state, action: PayloadAction<SetupSteps>) => {
      state.step = action.payload;
    },
    setJwt: (state, action: PayloadAction<string>) => {
      state.jwt = action.payload;
    },
    setJwtError: (state, action: PayloadAction<string>) => {
      state.jwtError = action.payload;
    },
    setEndpoint: (state, action: PayloadAction<string>) => {
      state.endpoint = action.payload;
    },
    setPsk: (state, action: PayloadAction<string>) => {
      state.psk = action.payload;
    },
    setCAHash: (state, action: PayloadAction<string>) => {
      state.caHash = action.payload;
    },
    setUIEndpoint: (state, action: PayloadAction<string>) => {
      state.uiEndpoint = action.payload;
    },
    setApiEndpoint: (state, action: PayloadAction<string>) => {
      state.apiEndpoint = action.payload;
    },
    setApiPort: (state, action: PayloadAction<number>) => {
      state.apiPort = action.payload;
    },
    setSiteName: (state, action: PayloadAction<string>) => {
      state.siteName = action.payload;
    },
    setUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    setPasswordRepeat: (state, action: PayloadAction<string>) => {
      state.passwordRepeat = action.payload;
    },
    resetSetupState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateLicense.pending, (state) => {
        state.jwtError = "";
        state.status = "loading";
      })
      .addCase(validateLicense.rejected, (state, action) => {
        state.status = "failed";
        const err = action.payload as ApiError;
        state.jwtError = err.message;
      })
      .addCase(validateLicense.fulfilled, (state, action) => {
        state.status = "idle";
        state.step = 2;
      })
      .addCase(setupConfigAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(setupConfigAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(setupConfigAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.apiEndpoint = action.payload.api_endpoint!;
        state.apiPort = action.payload.api_port!;
        state.uiEndpoint = action.payload.ui_endpoint!;
      })
      .addCase(finishSetupAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(finishSetupAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(finishSetupAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.setupComplete = true;
      })
      .addCase(joinControlPlaneAsync.pending, (state) => {
        state.joinError = null;
        state.status = "loading";
      })
      .addCase(joinControlPlaneAsync.rejected, (state, action) => {
        state.status = "failed";
        state.joinError = action.payload as ApiError;
        console.log(`state.joinError`, state.joinError);
      })
      .addCase(joinControlPlaneAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.setupComplete = true;
      });
  },
});

export const {
  setSetupPlan,
  setStep,
  setJwt,
  setJwtError,
  setEndpoint,
  setPsk,
  setCAHash,
  setUIEndpoint,
  setApiEndpoint,
  setApiPort,
  resetSetupState,
  setSiteName,
  setUsername,
  setPassword,
  setPasswordRepeat,
} = setupSlice.actions;

export default setupSlice.reducer;
